<script setup lang="ts">
import MessageBubble from './MessageBubble.vue';
import MessageWorkspaceSurface from './MessageWorkspaceSurface.vue';
import HeaderInfoTooltip from '@/components/workspace/HeaderInfoTooltip.vue';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { MessageSquare } from 'lucide-vue-next';
import { formatRelativeDate } from '~/lib/dateFormat';
import type { Message, MessageAttachment } from '~/types/models';

const props = withDefaults(defineProps<{
    messages?: Message[];
    messageCount?: number;
    loading?: boolean;
    eyebrow?: string;
    title: string;
    description?: string;
    outgoingDirection: Message['direction'];
    outgoingSenderName?: string;
    emptyTitle?: string;
    emptyDescription?: string;
    showAssistantContext?: boolean;
    canAddAttachmentsToProject?: boolean;
    canManageResponseFollowUps?: boolean;
    canDeleteMessages?: boolean;
    actionPathResolver?: (message: Message) => string;
    responseActionLoadingId?: string;
    searchQuery?: string;
    showSearch?: boolean;
}>(), {
    messages: () => [],
    messageCount: undefined,
    loading: false,
    eyebrow: 'Conversation',
    description: '',
    outgoingSenderName: '',
    emptyTitle: 'No messages yet',
    emptyDescription: 'Send the first message to begin the conversation.',
    showAssistantContext: true,
    canAddAttachmentsToProject: false,
    canManageResponseFollowUps: false,
    canDeleteMessages: false,
    actionPathResolver: undefined,
    responseActionLoadingId: '',
    searchQuery: '',
    showSearch: false,
});

const emit = defineEmits<{
    addAttachmentToProject: [messageId: string, attachment: MessageAttachment];
    markResponseNeeded: [message: Message];
    sendResponseReminder: [message: Message];
    resolveResponse: [message: Message];
    openMessageAction: [message: Message];
    deleteMessage: [message: Message];
    'update:searchQuery': [value: string];
}>();

function channelLabel(message: Message): string {
    if (message.entityType === 'form_submission' || message.entityType === 'portal_form_request') return 'Form';
    if (message.channel === 'email') return 'Email';
    if (message.channel === 'whatsapp') return 'WhatsApp';
    return 'App';
}

function channelVariant(channel?: Message['channel']): 'default' | 'secondary' | 'outline' {
    if (channel === 'email') return 'secondary';
    if (channel === 'whatsapp') return 'default';
    return 'outline';
}

function senderName(message: Message): string {
    if (message.direction === props.outgoingDirection && props.outgoingSenderName) return props.outgoingSenderName;
    return message.senderName || 'Unknown';
}

function actionPath(message: Message): string {
    if (message.automated && message.direction === props.outgoingDirection) return '';
    return props.actionPathResolver?.(message) || message.actionPath || '';
}
</script>

<template>
    <MessageWorkspaceSurface class="min-h-0 flex-1 overflow-hidden">
        <header class="shrink-0 pb-4">
            <div class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-2">
                    <span class="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <MessageSquare class="size-4" />
                    </span>
                    <h2 class="min-w-0 text-xl font-semibold tracking-tight">{{ title }}</h2>
                    <HeaderInfoTooltip
                        v-if="description"
                        :description="description"
                        :label="`About ${title}`"
                    />
                </div>
                <Badge variant="secondary">{{ messageCount ?? messages.length }} messages</Badge>
            </div>
        </header>

        <Input
            v-if="showSearch"
            type="search"
            aria-label="Search this conversation"
            placeholder="Search this conversation"
            class="mb-3 shrink-0"
            :model-value="searchQuery"
            @update:model-value="emit('update:searchQuery', String($event))"
        />

        <div v-if="loading" class="flex min-h-[18rem] flex-1 items-center justify-center text-sm text-muted-foreground">
            Loading conversation...
        </div>
        <div v-else class="min-h-0 flex-1 overflow-y-auto no-scrollbar">
            <EmptyState v-if="messages.length === 0" :title="emptyTitle" :description="emptyDescription">
                <template #icon><MessageSquare class="size-8" /></template>
            </EmptyState>
            <div v-else class="space-y-3">
                <MessageBubble
                    v-for="message in messages"
                    :key="message.id"
                    :message-id="message.id"
                    :direction="message.direction === outgoingDirection ? 'outgoing' : 'incoming'"
                    :sender-name="senderName(message)"
                    :channel-label="channelLabel(message)"
                    :channel-variant="channelVariant(message.channel)"
                    :timestamp="formatRelativeDate(message.createdAt)"
                    :subject="message.subject || ''"
                    :context-note="showAssistantContext && message.channel === 'email' && message.assistantContext ? 'AI used the full email during import and kept a distilled context for suggestions.' : ''"
                    :content="message.content"
                    :automated="message.automated === true"
                    :action-path="actionPath(message)"
                    :action-label="message.actionLabel || ''"
                    :inline-action-label="message.entityType === 'form_submission' ? 'View submission' : message.entityType === 'portal_form_request' ? 'View progress' : ''"
                    :attachments="message.attachments || []"
                    :can-add-attachments-to-project="Boolean(message.attachments?.length && canAddAttachmentsToProject)"
                    :can-manage-response="canManageResponseFollowUps && message.direction === outgoingDirection && message.automated !== true"
                    :can-delete="canDeleteMessages && message.entityType !== 'form_submission' && message.entityType !== 'portal_form_request'"
                    :response-required="message.responseRequired === true"
                    :response-status="message.responseStatus"
                    :response-requested-at="message.responseRequestedAt"
                    :response-action-loading="responseActionLoadingId === message.id"
                    @add-attachment-to-project="emit('addAttachmentToProject', message.id, $event)"
                    @mark-response-needed="emit('markResponseNeeded', message)"
                    @send-response-reminder="emit('sendResponseReminder', message)"
                    @resolve-response="emit('resolveResponse', message)"
                    @inline-action="emit('openMessageAction', message)"
                    @delete-message="emit('deleteMessage', message)"
                />
            </div>
        </div>
    </MessageWorkspaceSurface>
</template>
